import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/AuthContext";
import { ThemeProvider, CompanyProvider } from "@/store";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Feedback from "@/pages/Feedback";
import PublicQuote from "@/pages/PublicQuote";
import AdminLayout from "@/components/AdminLayout";
import EmpLayout from "@/components/EmpLayout";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Quotations from "@/pages/Quotations";
import Jobs from "@/pages/Jobs";
import Billing from "@/pages/Billing";
import Stock from "@/pages/Stock";
import Leaves from "@/pages/Leaves";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import Incentives from "@/pages/Incentives";
import Calendar from "@/pages/Calendar";
import Money from "@/pages/Money";
import Gallery from "@/pages/Gallery";
import Track from "@/pages/Track";
import Platform from "@/pages/Platform";
import Workspace from "@/pages/Workspace";
import Diagnostics from "@/pages/Diagnostics";
import DesignCheck from "@/pages/DesignCheck";
import Attendance from "@/pages/Attendance";
import Payroll from "@/pages/Payroll";
import MyHR from "@/pages/MyHR";
import Products from "@/pages/Products";
import Orders from "@/pages/Orders";
import EmpHome from "@/pages/emp/EmpHome";
import EmpJob from "@/pages/emp/EmpJob";
import EmpExpenses from "@/pages/emp/EmpExpenses";
import EmpLeaves from "@/pages/emp/EmpLeaves";
import EmpProfile from "@/pages/emp/EmpProfile";
import ModulePlaceholder from "@/pages/ModulePlaceholder";
import WorkspaceRecords from "@/pages/WorkspaceRecords";
import Reports from "@/pages/Reports";
import AuditLog from "@/pages/AuditLog";
import WorkspaceChooser from "@/pages/WorkspaceChooser";
import CheckIn from "@/pages/CheckIn";
import Welcome from "@/pages/Welcome";
import api from "@/api";

function Guard({ children, roles, permission, ownerOnly = false, allowCustom = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc }} replace />;
  const customRoleHasAccess = allowCustom && !!user.custom_role && Object.values(user.permissions || {}).some((actions) => actions.includes("view"));
  if (roles && !roles.includes(user.role) && !(ownerOnly && user.is_workspace_owner) && !(permission && (user.permissions?.[permission] || []).includes("view")) && !customRoleHasAccess) {
    return <Navigate to={user.role === "team_leader" ? "/app" : "/"} replace />;
  }
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "super_admin" && !user.active_tenant_id) return <Navigate to="/choose-workspace" replace />;
  return <Navigate to={user.role === "team_leader" ? "/app" : "/admin"} replace />;
}

function AttendanceRouteGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const owner = user && (user.role === "super_admin" || user.is_workspace_owner);
  const home = user?.role === "team_leader" ? "/app" : user?.role === "super_admin" && !user.active_tenant_id ? "/choose-workspace" : "/admin";

  useEffect(() => {
    if (!user || user === false || owner) {
      setStatus(null);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const refresh = () => {
      setLoading(true);
      api.get("/attendance/status").then((response) => {
        if (active) setStatus(response.data);
      }).catch(() => {
        if (active) setStatus(null);
      }).finally(() => { if (active) setLoading(false); });
    };
    refresh();
    window.addEventListener("attendance-updated", refresh);
    return () => {
      active = false;
      window.removeEventListener("attendance-updated", refresh);
    };
  }, [user, owner]);

  if (user === null || user === false) return children;
  if (owner) return location.pathname === "/check-in" ? <Navigate to={home} replace /> : children;
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Checking attendance…</div>;
  if (status?.blocking && !["/check-in", "/welcome"].includes(location.pathname)) {
    return <Navigate to="/check-in" state={{ from: location }} replace />;
  }
  if (location.pathname === "/check-in" && status && !status.requires_checkin) return <Navigate to={home} replace />;
  return children;
}

const OFFICE = ["super_admin", "admin", "telecaller", "accountant"];
const ADMIN = ["super_admin", "admin"];
const BILLING = ["super_admin", "admin", "accountant"];

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AttendanceRouteGate>
    <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/welcome" element={<Guard><Welcome /></Guard>} />
            <Route path="/check-in" element={<Guard><CheckIn /></Guard>} />
            <Route path="/choose-workspace" element={<Guard roles={["super_admin"]}><WorkspaceChooser /></Guard>} />
            <Route path="/feedback/:token" element={<Feedback />} />
            <Route path="/q/:token" element={<PublicQuote />} />
            <Route path="/track/:token" element={<Track />} />
            <Route path="/admin" element={<Guard roles={OFFICE} allowCustom><AdminLayout /></Guard>}>
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<Leads />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="jobs" element={<Jobs />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="products" element={<Products />} />
              <Route path="orders" element={<Orders />} />
              <Route path="platform" element={<Guard roles={["super_admin"]}><Platform /></Guard>} />
              <Route path="billing" element={<Guard roles={BILLING}><Billing /></Guard>} />
              <Route path="money" element={<Guard roles={["super_admin"]} permission="money" ownerOnly><Money /></Guard>} />
              <Route path="purchase" element={<Guard roles={["super_admin"]} permission="purchase" ownerOnly><WorkspaceRecords module="purchase" /></Guard>} />
              <Route path="expenses" element={<Guard roles={["super_admin"]} permission="expenses" ownerOnly><WorkspaceRecords module="expenses" /></Guard>} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="stock" element={<Stock />} />
              <Route path="leaves" element={<Leaves />} />
              <Route path="users" element={<Guard roles={ADMIN}><Users /></Guard>} />
              <Route path="hr" element={<Guard roles={ADMIN}><Users /></Guard>} />
              <Route path="settings" element={<Guard roles={ADMIN}><Settings /></Guard>} />
              <Route path="workspace" element={<Guard roles={ADMIN}><Workspace /></Guard>} />
              <Route path="diagnostics" element={<Guard roles={["super_admin"]}><Diagnostics /></Guard>} />
              <Route path="design-check" element={<Guard roles={["super_admin"]}><DesignCheck /></Guard>} />
              <Route path="attendance" element={<Guard roles={ADMIN}><Attendance /></Guard>} />
              <Route path="payroll" element={<Guard roles={["super_admin"]} permission="payroll" ownerOnly><Payroll /></Guard>} />
              <Route path="myhr" element={<MyHR />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="incentives" element={<Guard roles={ADMIN}><Incentives /></Guard>} />
              <Route path="customers" element={<Guard roles={OFFICE} permission="customers"><WorkspaceRecords module="customers" /></Guard>} />
              <Route path="vendors" element={<Guard roles={["super_admin"]} permission="vendors" ownerOnly><WorkspaceRecords module="vendors" /></Guard>} />
              <Route path="amc-warranty" element={<Guard roles={OFFICE} permission="amc_warranty"><WorkspaceRecords module="amc_warranty" /></Guard>} />
              <Route path="tasks" element={<Guard roles={OFFICE} permission="tasks_reminders"><WorkspaceRecords module="tasks_reminders" /></Guard>} />
              <Route path="documents" element={<Guard roles={OFFICE} permission="documents"><WorkspaceRecords module="documents" /></Guard>} />
              <Route path="reports" element={<Guard roles={["super_admin", "admin", "accountant"]} permission="reports"><Reports /></Guard>} />
              <Route path="feedback" element={<Guard roles={OFFICE} permission="feedback"><WorkspaceRecords module="feedback" /></Guard>} />
              <Route path="audit-log" element={<Guard roles={["super_admin"]} permission="audit_logs" ownerOnly><AuditLog /></Guard>} />
              <Route path="recycle-bin" element={<Guard roles={["super_admin"]} permission="recycle_bin" ownerOnly><WorkspaceRecords module="recycle_bin" /></Guard>} />
              <Route path="help-support" element={<Guard roles={OFFICE} permission="help_support"><WorkspaceRecords module="help_support" /></Guard>} />
            </Route>
            <Route path="/app" element={<Guard roles={["team_leader"]}><EmpLayout /></Guard>}>
              <Route index element={<EmpHome />} />
              <Route path="job/:id" element={<EmpJob />} />
              <Route path="expenses" element={<EmpExpenses />} />
              <Route path="leaves" element={<EmpLeaves />} />
              <Route path="profile" element={<EmpProfile />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </AttendanceRouteGate>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <CompanyProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </CompanyProvider>
        </ThemeProvider>
        <Toaster richColors position="top-center" />
      </AuthProvider>
    </div>
  );
}
